﻿import {DateOnly} from "@2d-front-monorepo/core";
import React, {ChangeEvent, JSX, useCallback, useMemo} from "react";
import {useControlId} from "../helpers/useControlId";
import DatePicker from "../HtmlControls/DatePicker";
import {MvvmPrefixedControlProps} from "./MvvmControlProps";

import {useRegisterValidation} from "./useRegisterValidation";

export type MvvmDatePickerProps<T extends object = never> = MvvmPrefixedControlProps<T, DateOnly> & {
	min?: DateOnly;
	max?: DateOnly;
	isSelectable?: (date: DateOnly) => boolean;
	dayClassName?: (date: DateOnly) => string | undefined;
};

export default function MvvmDatePicker<T extends object = never>(props: MvvmDatePickerProps<T>): JSX.Element {
	const {
		context,
		property,
		id,
		label,
		hint,
		column,
		controlPrefix,
		controlSuffix,
		hidden,
		disabled,
		validation,
		min,
		max,
		isSelectable,
		dayClassName
	} = props;

	const controlId = useControlId(id);
	const value = context.getValue<DateOnly>(property)?.toJSON() ?? "";

	const [minValue, maxValue] = useMemo(() => {
		const minValue = min == null ? undefined : min.toJSON();
		const maxValue = max == null ? undefined : max.toJSON();
		return [minValue, maxValue];
	}, [min, max]);

	const onChange = useCallback(function (e: ChangeEvent<HTMLInputElement>): void {
		context.setValue(property, DateOnly.fromIsoString(e.target.value), true);
	}, [context, property, validation, controlId]);

	useRegisterValidation(context, property, controlId, validation);

	return <DatePicker label={label}
	                   name={property}
	                   hint={hint}
	                   column={column}
	                   controlPrefix={controlPrefix}
	                   controlSuffix={controlSuffix}
	                   hidden={hidden}
	                   disabled={disabled}
	                   min={minValue}
	                   max={maxValue}
	                   id={id}
	                   required={validation?.required != null}
	                   error={context.getError(property)?.error}
	                   isSelectable={isSelectable}
	                   dayClassName={dayClassName}
	                   value={value}
	                   onChange={onChange}/>;
};
